import { BASE_API_URL } from '../apiConfig';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CreateProposal = () => {
    const { t } = useTranslation();
    const { leagueId } = useParams();
    const locationState = useLocation().state;
    const navigate = useNavigate();

    const [slots, setSlots] = useState([{ startTime: '', maxPlayers: 10, location: '', address: '', lat: null, lng: null, id: Date.now() }]);
    const [previousLocations, setPreviousLocations] = useState([]);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(null);
    const [highlightedSlotId, setHighlightedSlotId] = useState(null);

    useEffect(() => {
        // Se arriviamo con un caricamento di un match base (state), inizializziamo lo slot
        if (locationState && locationState.baseMatch) {
            const m = locationState.baseMatch;
            const nextWeek = new Date(m.start_time);
            nextWeek.setDate(nextWeek.getDate() + 7);

            // Format to datetime-local (YYYY-MM-DDTHH:mm)
            const localDate = new Date(nextWeek.getTime() - (nextWeek.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);

            setSlots([{
                startTime: localDate,
                maxPlayers: m.max_players || 10,
                location: m.location || '',
                address: m.address || '',
                lat: m.lat || null,
                lng: m.lng || null,
                id: Date.now()
            }]);
        }
    }, [locationState]);

    useEffect(() => {
        const fetchLocations = async () => {
            const token = localStorage.getItem('token');
            try {
                const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/locations`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setPreviousLocations(data);

                    // In fase iniziale, se gli slot sono già popolati (es. da ultimo match),
                    // ma non hanno prezzo, proviamo a recuperarlo ora che abbiamo lo storico
                    setSlots(prev => prev.map(s => {
                        if (s.location && !s.price) {
                            const match = data.find(loc =>
                                loc.location.toLowerCase() === s.location.toLowerCase() &&
                                Number(loc.max_players) === Number(s.maxPlayers)
                            );
                            if (match) return { ...s, price: match.price };
                        }
                        return s;
                    }));
                }
            } catch (err) {
                console.error("Errore caricamento locations:", err);
            }
        };
        fetchLocations();
    }, [leagueId]);

    const lastSlotRef = React.useRef(null);

    const addSlot = () => {
        const lastSlot = slots[slots.length - 1]; // Torniamo a prendere l'ultimo
        let nextStartTime = '';
        let nextMaxPlayers = 10;
        let nextLocation = '';
        let nextAddress = '';

        if (lastSlot && lastSlot.startTime) {
            const lastDate = new Date(lastSlot.startTime);
            const nextDate = new Date(lastDate.getTime() + 24 * 60 * 60 * 1000);
            nextStartTime = new Date(nextDate.getTime() - (nextDate.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
            nextMaxPlayers = lastSlot.maxPlayers;
            nextLocation = lastSlot.location;
            nextAddress = lastSlot.address;
        }

        const newId = Date.now();
        setSlots([...slots, {
            startTime: nextStartTime,
            maxPlayers: nextMaxPlayers,
            location: nextLocation,
            address: nextAddress,
            lat: lastSlot.lat || null,
            lng: lastSlot.lng || null,
            id: newId
        }]);

        setHighlightedSlotId(newId);
        setTimeout(() => setHighlightedSlotId(null), 1000);
    };

    // Effetto per scrollare verso il nuovo slot quando viene aggiunto
    useEffect(() => {
        if (highlightedSlotId && lastSlotRef.current) {
            lastSlotRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [highlightedSlotId]);

    const removeSlot = (index) => {
        if (slots.length > 1) {
            setSlots(slots.filter((_, i) => i !== index));
        }
    };

    const updateSlot = (index, field, value) => {
        const newSlots = [...slots];
        newSlots[index] = { ...newSlots[index], [field]: value };

        // Se cambia location o maxPlayers, proviamo ad auto-popolare il prezzo dallo storico
        if (field === 'maxPlayers' || field === 'location') {
            const locName = field === 'location' ? value : newSlots[index].location;
            const players = field === 'maxPlayers' ? value : newSlots[index].maxPlayers;

            if (locName) {
                const historyMatch = previousLocations.find(loc =>
                    loc.location.toLowerCase() === locName.toLowerCase() &&
                    Number(loc.max_players) === Number(players)
                );
                if (historyMatch && historyMatch.price) {
                    newSlots[index].price = historyMatch.price;
                }
            }
        }
        setSlots(newSlots);
    };

    const handleSelectSuggestion = (index, loc) => {
        const newSlots = [...slots];

        // Cerchiamo nello storico il prezzo corretto per questa location in base al numero di persone attuale dello slot
        const matchingHistory = previousLocations.find(p =>
            p.location === loc.location &&
            Number(p.max_players) === Number(newSlots[index].maxPlayers)
        );

        newSlots[index] = {
            ...newSlots[index],
            location: loc.location,
            address: loc.address || '',
            lat: loc.lat || null,
            lng: loc.lng || null,
            price: matchingHistory ? matchingHistory.price : newSlots[index].price
        };
        setSlots(newSlots);
        setActiveSuggestionIndex(null);
    };
    const handleAddressBlur = async (index) => {
        const slot = slots[index];
        if (!slot.address || slot.address.trim() === '') return;

        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(slot.address)}&limit=1`);
            const data = await response.json();
            if (data && data.length > 0) {
                const newSlots = [...slots];
                newSlots[index] = {
                    ...newSlots[index],
                    lat: parseFloat(data[0].lat),
                    lng: parseFloat(data[0].lon)
                };
                setSlots(newSlots);
            }
        } catch (error) {
            console.error("Geocoding error:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            // Convertiamo le date locali dell'input in ISO UTC in modo deterministico
            const submittedSlots = slots.map(s => {
                if (!s.startTime) return { ...s, startTime: null };
                const [datePart, timePart] = s.startTime.split('T');
                const [y, m, d] = datePart.split('-');
                const [h, min] = timePart.split(':');
                const localDate = new Date(y, m - 1, d, h, min);
                return {
                    ...s,
                    startTime: localDate.toISOString()
                };
            });

            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/proposals`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ slots: submittedSlots })
            });
            if (res.ok) navigate(`/league/${leagueId}`);
        } catch (err) {
            console.error("Errore salvataggio proposta:", err);
        }
    };

    return (
        <div className="proposal-page">
            <header className="page-header">
                <h1>{t('propose_match_times')}</h1>
                <p className="subtitle">{t('propose_match_subtitle')}</p>
            </header>

            <form onSubmit={handleSubmit} className="proposal-form">
                <div className="slots-grid">
                    {slots.map((slot, index) => (
                        <div
                            key={slot.id || index}
                            ref={index === slots.length - 1 ? lastSlotRef : null}
                            className={`slot-input-card card ${highlightedSlotId === slot.id ? 'highlight-new-slot' : ''}`}
                        >
                            <div className="slot-header">
                                <h3>Opzione #{index + 1}</h3>
                                {slots.length > 1 && (
                                    <button type="button" className="remove-btn" onClick={() => removeSlot(index)}>✕</button>
                                )}
                            </div>
                            <div className="slot-fields">
                                <div className="form-group">
                                    <label>{t('date_time')}</label>
                                    <input
                                        type="datetime-local"
                                        value={slot.startTime}
                                        onChange={(e) => updateSlot(index, 'startTime', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('available_spots')}</label>
                                    <input
                                        type="number"
                                        value={slot.maxPlayers}
                                        onChange={(e) => updateSlot(index, 'maxPlayers', parseInt(e.target.value))}
                                        required
                                    />
                                </div>
                                <div className="form-group suggestion-wrapper">
                                    <label>{t('location')}</label>
                                    <input
                                        type="text"
                                        placeholder={t('location_placeholder')}
                                        value={slot.location || ''}
                                        onFocus={() => setActiveSuggestionIndex(index)}
                                        onBlur={() => setTimeout(() => setActiveSuggestionIndex(null), 200)}
                                        onChange={(e) => updateSlot(index, 'location', e.target.value)}
                                        required
                                    />
                                    {activeSuggestionIndex === index && previousLocations.length > 0 && (
                                        <div className="suggestions-dropdown">
                                            {(() => {
                                                const uniqueLocations = [];
                                                const seen = new Set();
                                                previousLocations.forEach(loc => {
                                                    if (!seen.has(loc.location.toLowerCase())) {
                                                        seen.add(loc.location.toLowerCase());
                                                        uniqueLocations.push(loc);
                                                    }
                                                });
                                                return uniqueLocations
                                                    .filter(loc => loc.location.toLowerCase().includes(slot.location?.toLowerCase() || ''))
                                                    .map((loc, i) => (
                                                        <div
                                                            key={i}
                                                            className="suggestion-item"
                                                            onClick={() => handleSelectSuggestion(index, loc)}
                                                        >
                                                            <strong>{loc.location}</strong>
                                                            {loc.address && <span className="suggestion-address">{loc.address}</span>}
                                                        </div>
                                                    ))
                                            })()}
                                        </div>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>{t('address')} ({t('optional')})</label>
                                    <input
                                        type="text"
                                        placeholder={t('address_placeholder')}
                                        value={slot.address || ''}
                                        onChange={(e) => updateSlot(index, 'address', e.target.value)}
                                        onBlur={() => handleAddressBlur(index)}
                                    />
                                    {slot.lat && <small style={{ color: '#2e7d32' }}>✅ {t('address_found')}</small>}
                                </div>
                                <div className="form-group">
                                    <label>{t('total_cost_label')} ({t('optional')})</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={slot.price || ''}
                                        onChange={(e) => updateSlot(index, 'price', e.target.value === '' ? null : parseFloat(e.target.value))}
                                        placeholder={t('total_cost_placeholder')}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="form-actions">
                    <button type="button" className="secondary-btn" onClick={addSlot}>
                        {t('add_another_option')}
                    </button>
                    <button type="submit" className="primary-btn submit-proposal">
                        {t('send_proposal_to_league')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateProposal;
